// DAY-DIRECTSTAFF-2026-10-30b: the candidate PROFILE in the CRM (migrations/2026-11-02-candidate-profile.sql, applied by the hub
// 2026-11-02). The candidate fills it on the onboard portal at /p/<token>; here staff see the 7 items, send the link, read the
// references one candidate at a time, and download a submission summary for a facility.
//   status      public.profile_status (one row per candidate with a profile; the same rule the candidate's page reads)
//   links       public.profile_sessions (staff select): the newest per candidate is "the link"; the token is never stored
//   send        profile_start(candidate_id) -> { session_id, token, expires_at }; "New link, keep progress" = profile_relink(session_id)
//   references  profile_references(candidate_id) -- on tap only, never kept, never on the pipeline card, never in an export;
//               profile_reference_contacted(candidate_id, position) ticks one and writes the activity log (the RPC does)
// Nothing here reads a date of birth or the last four of the SSN: the status says "on file" and that is all there is.
import PT from './profileSkillsPT.json';
// 2026-11-03: Matt approved the five drafts; the portal's public/skills/<DISC>.json, vendored byte for byte like PT's
import PTA from './profileSkillsPTA.json';
import OT from './profileSkillsOT.json';
import COTA from './profileSkillsCOTA.json';
import SLP from './profileSkillsSLP.json';
import SLPA from './profileSkillsSLPA.json';

export const PROFILE_LOG_TYPE = 'profile';
export const NOT_STARTED_DAYS = 3;
export const CHECKLISTS = { PT, PTA, OT, COTA, SLP, SLPA };   // the live ones (the portal's LIVE list): all six since 2026-11-03
export const SETTINGS = [['acute', 'Acute care'], ['inpatient_rehab', 'Inpatient rehab'], ['snf_ltc', 'SNF / long-term care'],
  ['outpatient', 'Outpatient ortho'], ['home_health', 'Home health'], ['schools_peds', 'Schools / pediatrics']];   // the portal's words
export const ITEMS = [
  ['resume', 'Resume'], ['skills', 'Skills checklist'], ['start', 'Start date and time off'], ['identity', 'Date of birth and SSN last 4'],
  ['bls', 'BLS / CPR card'], ['references', 'References'], ['finished', 'Finished'],
];
const ERRORS = {
  'profile:rate_limited': 'Five profile links were already made for them today. Use the newest one, or try tomorrow.',
  'profile:no_such_candidate': 'That candidate is no longer there. Refresh.',
  'profile:link_revoked': 'That link was turned off. Send a new profile link instead.',
  'profile:not_newest': 'A newer link was made since. Refresh to see it.',
  'profile:no_such_link': 'That link is no longer there. Refresh.',
  'profile:no_such_reference': 'That reference is no longer there. Refresh.',
};
export const plainError = (e) => { const m = /profile:[a-z_]+/.exec(String((e && e.message) || '')); return (m && ERRORS[m[0]]) || (e && e.message) || 'That did not work.'; };
export const codeOf = (e) => { const m = /profile:[a-z_]+/.exec(String((e && e.message) || '')); return m ? m[0] : ''; };

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const t = (iso) => { const x = Date.parse(iso || ''); return Number.isNaN(x) ? null : x; };
export const dayText = (d) => (s(d) ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(s(d)) ? `${s(d)}T12:00:00Z` : d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }) : '');
export const profileUrl = (token, base) => `${String(base || 'https://onboard.protherapystaffing.com').replace(/\/$/, '')}/p/${token}`;
const hi = (name) => { const f = s(name).split(/\s+/)[0] || ''; return /^[A-Za-z][A-Za-z'.-]*$/.test(f) ? `Hi ${f},` : 'Hi,'; };
export function profileMessage(name, link) {
  return `${hi(name)} to put you forward for jobs we need your profile: your resume, a skills checklist, your start date, your BLS card and three references. You can stop and come back to it: ${link} (the link lasts 7 days). Thank you!`;
}
export function profileReminder(name, link) {
  return `${hi(name)} a reminder about your ProTherapy profile, so we can put you forward for jobs: ${link} (a new link, it lasts 7 days). You can stop and come back to it. Reply here with any questions.`;
}
export const profileNote = (sessionId, channel, by) => `Profile link ${sessionId} sent by ${channel === 'copy' ? 'copied link' : channel}${by ? ` by ${by}` : ''}`;

/** The 7 items from a profile_status row -> [{ key, label, state: 'done' | 'missing' | 'expired', detail }] */
export function profileItems(row) {
  const r = row || {};
  const refs = Number(r.references_count) || 0;
  const one = (key, state, detail = '') => ({ key, label: ITEMS.find(([k]) => k === key)[1], state, detail });
  return [
    one('resume', r.resume_on_file ? 'done' : 'missing', r.resume_on_file && r.resume_uploaded_at ? `uploaded ${dayText(r.resume_uploaded_at)}` : ''),
    one('skills', r.skills_done ? 'done' : 'missing', r.skills_done ? `${r.discipline ? `${r.discipline}, ` : ''}signed ${dayText(r.skills_attested_at)}` : r.discipline ? `${r.discipline}, not signed` : ''),
    one('start', r.start_date_set ? 'done' : 'missing', r.start_date_set ? `from ${dayText(r.available_from)}${Number(r.time_off_count) ? ` · ${r.time_off_count} time off` : ''}` : ''),
    one('identity', r.dob_on_file && r.ssn_last4_on_file ? 'done' : 'missing',
      r.dob_on_file && r.ssn_last4_on_file ? 'both on file' : r.dob_on_file ? 'SSN last 4 missing' : r.ssn_last4_on_file ? 'date of birth missing' : ''),
    one('bls', r.bls_expired ? 'expired' : r.bls_on_file ? 'done' : 'missing', r.bls_on_file && r.bls_expires_on ? `${r.bls_expired ? 'expired' : 'expires'} ${dayText(r.bls_expires_on)}` : ''),
    one('references', r.references_done ? 'done' : 'missing', `${refs} of 3 complete`),
    one('finished', r.profile_completed_at ? 'done' : 'missing', r.profile_completed_at ? dayText(r.profile_completed_at) : ''),
  ];
}
export const doneCount = (row) => profileItems(row).filter((i) => i.state === 'done').length;

/** The newest link per candidate (profile_sessions rows) -> Map(candidate_id -> row) */
export function newestLinks(sessions = []) {
  const m = new Map();
  for (const x of sessions || []) {
    const k = String(x.candidate_id);
    const cur = m.get(k);
    if (!cur || s(x.created_at) > s(cur.created_at) || (s(x.created_at) === s(cur.created_at) && Number(x.id) > Number(cur.id))) m.set(k, x);
  }
  return m;
}
/** The link line: '' (none) | "Link sent Nov 2, open until Nov 9 · opened Nov 3" | "... turned off" | "... expired Nov 9" */
export function linkLine(link, now = Date.now()) {
  if (!link) return '';
  const sent = `Link sent ${dayText(link.created_at)}`;
  if (link.revoked_at) return `${sent}, turned off ${dayText(link.revoked_at)}`;
  const exp = t(link.expires_at);
  const opened = link.first_opened_at ? `opened ${dayText(link.first_opened_at)}` : 'not opened yet';
  return exp !== null && exp < now ? `${sent}, expired ${dayText(link.expires_at)} · ${opened}` : `${sent}, open until ${dayText(link.expires_at)} · ${opened}`;
}
/** "New link, keep progress" is for a link that exists and was not turned off; otherwise a new one is made. */
export const canRelink = (link) => !!(link && !link.revoked_at);

const isTest = (c) => !c || (Array.isArray(c.tags) && c.tags.some((x) => s(x).toLowerCase() === 'test')) || !!c.merged_into || !!c.test_marked_at;

/** Today: "Profile not started after 3 days" -- the newest link, made 3+ days ago, never opened, not turned off, not finished. */
export function notStarted({ sessions = [], status = [], candidates = [], now = Date.now() }) {
  const byCand = new Map((candidates || []).map((c) => [String(c.id), c]));
  const st = new Map((status || []).map((r) => [String(r.candidate_id), r]));
  const out = [];
  for (const [cid, link] of newestLinks(sessions)) {
    const c = byCand.get(cid);
    if (isTest(c) || link.revoked_at || link.first_opened_at) continue;
    const made = t(link.created_at);
    if (made === null || now - made < NOT_STARTED_DAYS * 864e5) continue;
    const r = st.get(cid);
    if (r && (r.profile_completed_at || r.submission_ready)) continue;
    out.push({ candidate: c, link, days: Math.floor((now - made) / 864e5) });
  }
  return out.sort((a, b) => s(a.link.created_at).localeCompare(s(b.link.created_at)));
}

/** DAY-DIRECTSTAFF-2026-11-08 §3: Today's "Not opened" -- the newest link never opened (any age), not turned off, not finished or ready;
 *  test candidates out (the same links as the Profiles line's "opened"). -> [{ candidate, link, days, email, expired }], oldest first */
export function notOpened({ sessions = [], status = [], candidates = [], now = Date.now() }) {
  const byCand = new Map((candidates || []).map((c) => [String(c.id), c]));
  const st = new Map((status || []).map((r) => [String(r.candidate_id), r]));
  const out = [];
  for (const [cid, link] of newestLinks(sessions)) {
    const c = byCand.get(cid);
    if (isTest(c) || link.revoked_at || link.first_opened_at) continue;
    const r = st.get(cid);
    if (r && (r.profile_completed_at || r.submission_ready)) continue;
    const made = t(link.created_at); const exp = t(link.expires_at);
    out.push({ candidate: c, link, days: made === null ? 0 : Math.max(0, Math.floor((now - made) / 864e5)), email: !!s(c.email), expired: exp !== null && exp < now });
  }
  return out.sort((a, b) => s(a.link.created_at).localeCompare(s(b.link.created_at)));
}

/**
 * §3 "Resend by email", one tap: checks first (an email on file, not No email, the company email up), THEN a new link to the same
 * profile (make(): CandidateProfile.jsx makeProfileLink -- "New link, keep progress"; the older link stops working), the reminder
 * email, the timeline line. sendApi: lib/sendLink.js; api: makeProfileApi. -> { sessionId, to, logError? }; throws Error with .code
 * (no_email | opted_out | email_not_ready) before anything is made.
 */
export async function resendByEmail({ candidate, make, sendApi, api, by = '' }) {
  const no = (code, msg) => { const e = new Error(msg); e.code = code; return e; };
  const to = s(candidate && candidate.email);
  if (!to) throw no('no_email', 'No email on file.');
  if (await sendApi.optedOut(candidate)) throw no('opted_out', 'They are marked No email, so nothing was sent.');
  if (!(await sendApi.emailReady())) throw no('email_not_ready', 'The company email is not available right now; nothing was sent.');
  const made = await make();
  await sendApi.email('profile', to, profileReminder(candidate.name, made.link), candidate.id);
  try { await api.logSent(candidate.id, made.sessionId, 'email', by); } catch (e) { return { sessionId: made.sessionId, to, logError: e.message }; }
  return { sessionId: made.sessionId, to };
}

/** Numbers: this week (the 7 days to now) and last week -- profiles sent (candidates whose link was made), started (whose link
 *  was first opened), submission-ready (ready now, last changed in that week). Test candidates left out. Counts only. */
export function profileNumbers({ sessions = [], status = [], testIds = new Set(), now = Date.now() }) {
  const W = 7 * 864e5;
  const inWeek = (iso, k) => { const x = t(iso); return x !== null && x <= now - k * W && x > now - (k + 1) * W; };
  const keep = (cid) => !testIds.has(String(cid));
  const count = (rows, at, k) => new Set(rows.filter((r) => keep(r.candidate_id) && inWeek(at(r), k)).map((r) => String(r.candidate_id))).size;
  const ready = (status || []).filter((r) => r.submission_ready);
  const row = (id, label, at, rows) => ({ id, label, now: count(rows, at, 0), last: count(rows, at, 1) });
  return [
    row('sent', 'Profiles sent', (r) => r.created_at, sessions || []),
    row('started', 'Started (opened the link)', (r) => r.first_opened_at, sessions || []),
    row('ready', 'Submission-ready', (r) => r.updated_at, ready),
  ];
}

// DAY-DIRECTSTAFF-2026-11-04 §4: the Monday brief's own profile count, the brief's rule (pt-agents agents/morning_brief.py section_profiles):
// "this week" = candidates with the agents' first-link marker (agent_lead_decisions decision 'profile_link', reason 'first:c<id>')
// in the 7 days to now (last week: the 7 before); of those, started = opened or an item done, ready = submission_ready, read from
// profile_status now. As the brief: agent links only (a link staff made is in "Profiles sent" above), test candidates not left out.
const FIRST_RE = /^first:c(\d{1,18})$/;
export function briefProfileCounts({ decisions = [], status = [], now = Date.now() }) {
  const W = 7 * 864e5;
  const st = new Map((status || []).map((r) => [String(r.candidate_id), r]));
  const ids = (k) => { const out = new Set();
    for (const d of decisions || []) { if (!d || s(d.decision) !== 'profile_link') continue; const m = FIRST_RE.exec(s(d.reason)); const x = t(d.created_at);
      if (m && x !== null && x >= now - (k + 1) * W && x < now - k * W + (k === 0 ? 1 : 0)) out.add(m[1]); }
    return [...out]; };
  const started = (list) => list.filter((i) => { const r = st.get(i); return !!r && (!!r.link_first_opened_at || Number(r.items_done || 0) > 0); }).length;
  const ready = (list) => list.filter((i) => { const r = st.get(i); return !!r && r.submission_ready === true; }).length;
  const a = ids(0); const b = ids(1);
  return [
    { id: 'brief_sent', label: "The agents' links (the Monday brief's \"this week\")", now: a.length, last: b.length },
    { id: 'brief_started', label: '  of those, started (opened or an item done)', now: started(a), last: started(b) },
    { id: 'brief_ready', label: '  of those, ready to submit', now: ready(a), last: ready(b) },
  ];
}

/** The thread view's suggestion: this conversation has a hand-off where they said they want to move forward (application_intent). */
export const INTERESTED_REASONS = new Set(['application_intent']);
export const interestedHandoff = (notifications = [], conversationId = null) =>
  !!conversationId && (notifications || []).some((n) => String(n.conversation_id) === String(conversationId) && INTERESTED_REASONS.has(n.reason));
const digits = (v) => String(v || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
/** The candidate record for a lead: the same 10-digit number, else the same email; never a test or merged record. */
export function candidateForLead(lead, candidates = []) {
  const pool = (candidates || []).filter((c) => !isTest(c));
  const p = digits(lead && lead.phone);
  if (p.length === 10) { const c = pool.find((x) => digits(x.phone) === p); if (c) return c; }
  const e = s(lead && lead.email).toLowerCase();
  return (e && e.includes('@') && pool.find((x) => s(x.email).toLowerCase() === e)) || null;
}
/** Suggest "Send profile link" unless they are already submission-ready or hold an open link. */
export const suggestProfile = (row) => !(row && (row.submission_ready || row.link_open));

// ---------------------------------------------------------------------------------------------------------------------------
// The submission summary: ONE PDF for a facility. What it may carry is listed here and nowhere else: name, discipline, licence
// states, available from, time off (dates only), the skills checklist summary (experience by setting; self-rating by area),
// the BLS card on file with its expiry, and the resume attached. NO date of birth, NO SSN, NO references -- one line says
// references are available on request. summaryModel() copies only those fields, whatever it is handed.
// ---------------------------------------------------------------------------------------------------------------------------
export const REFERENCES_LINE = 'References: available on request.';
const listOf = (v) => (Array.isArray(v) ? v : s(v) ? s(v).split(/[,;/\s]+/) : []).map((x) => s(x).toUpperCase()).filter((x) => /^[A-Z]{2}$/.test(x));
const avg = (a) => (a.length ? Math.round((a.reduce((x, y) => x + y, 0) / a.length) * 10) / 10 : null);

/** candidate (the CRM row), documents (onboarding_documents rows for them), profile (candidate_profiles row), row (profile_status) */
export function summaryModel({ candidate = {}, documents = [], profile = null, row = null, now = Date.now() }) {
  const docs = documents || [];
  const states = [...new Set([...listOf(candidate.licenses), ...docs.filter((d) => ['license', 'compact_privilege'].includes(d.kind)).map((d) => s(d.license_state).toUpperCase())
    .filter((x) => /^[A-Z]{2}$/.test(x))])].sort();
  const newest = (kind) => docs.filter((d) => d.kind === kind).sort((a, b) => s(b.uploaded_at).localeCompare(s(a.uploaded_at)))[0] || null;
  const bls = newest('cpr');
  const resume = newest('resume');
  const timeOff = (Array.isArray(candidate.time_off) ? candidate.time_off : []).filter((x) => x && /^\d{4}-\d{2}-\d{2}$/.test(s(x.from)))
    .map((x) => ({ from: s(x.from), to: /^\d{4}-\d{2}-\d{2}$/.test(s(x.to)) ? s(x.to) : s(x.from) }));      // dates only: the note stays with us
  const p = profile || {};
  const list = p.skills_attested_at && CHECKLISTS[p.discipline] ? CHECKLISTS[p.discipline] : null;
  const ratings = p.skills && typeof p.skills === 'object' ? p.skills : {};
  const areas = list ? list.sections.map((sec) => {
    const vals = sec.items.map((i) => Number(ratings[i.key])).filter((v) => v >= 1 && v <= 4);
    return { title: sec.title.charAt(0) + sec.title.slice(1).toLowerCase(), average: avg(vals), independent: vals.filter((v) => v === 4).length, rated: vals.length, items: sec.items.length };
  }) : [];
  const settings = SETTINGS.map(([k, label]) => {
    const v = p.settings && p.settings[k];
    const years = v && v.years !== null && v.years !== undefined && v.years !== '' ? Number(v.years) : null;
    const last = v && /^\d{4}$/.test(s(v.last)) ? s(v.last) : '';
    return years === null && !last ? null : { label, years, last };
  }).filter(Boolean);
  const blsDate = bls ? s(bls.expires_on) : '';
  return {
    name: s(candidate.name),
    discipline: s(p.discipline) || s(candidate.specialty),
    states,
    availableFrom: s(candidate.available_from) || s(row && row.available_from),
    timeOff,
    skills: list ? { discipline: p.discipline, signedAt: s(p.skills_attested_at), scale: list.scale, areas, settings } : null,
    settings,
    bls: bls ? { expiresOn: blsDate, expired: !!blsDate && blsDate < new Date(now).toISOString().slice(0, 10) } : null,
    resume: resume ? { path: s(resume.storage_path), type: (/\.([a-z]+)$/i.exec(s(resume.storage_path)) || [])[1] || '' } : null,
    preparedOn: new Date(now).toISOString().slice(0, 10),
  };
}
const range = (x) => (x.from === x.to ? dayText(x.from) : `${dayText(x.from)} to ${dayText(x.to)}`);
const yearsText = (y) => (y === null ? '' : `${y} year${y === 1 ? '' : 's'}`);
/** Every line the PDF draws, in order: [{ text, style: 'title' | 'head' | 'body' | 'small' }] -- the PDF draws these and nothing else. */
export function summaryLines(m, { resumePages = 0 } = {}) {
  const L = [];
  const add = (text, style = 'body') => L.push({ text, style });
  add('Candidate summary', 'title');
  add(`ProTherapy Staffing · prepared ${dayText(m.preparedOn)}`, 'small');
  add(m.name || 'Name not on file', 'head');
  add(`Discipline: ${m.discipline || 'not on file'}`);
  add(`Licensed in: ${m.states.length ? m.states.join(', ') : 'not on file'}`);
  add(`Available from: ${m.availableFrom ? dayText(m.availableFrom) : 'not on file'}`);
  add(`Time off: ${m.timeOff.length ? m.timeOff.map(range).join('; ') : 'none'}`);
  add(`BLS / CPR card: ${m.bls ? (m.bls.expiresOn ? `on file, ${m.bls.expired ? 'EXPIRED' : 'expires'} ${dayText(m.bls.expiresOn)}` : 'on file') : 'not on file'}`);
  add('Skills checklist', 'head');
  if (m.skills) {
    add(`${m.skills.discipline} checklist, signed by the candidate ${dayText(m.skills.signedAt)}. Self-rating 1 to 4: ${m.skills.scale.map((x) => `${x.value} ${x.label}`).join(' · ')}.`, 'small');
    for (const a of m.skills.areas) add(`${a.title}: average ${a.average === null ? '-' : a.average.toFixed(1)} · ${a.independent} of ${a.items} rated Independent / Proficient`);
  } else add('Not completed yet.');
  add('Experience by setting', 'head');
  if (m.settings.length) for (const x of m.settings) add(`${x.label}: ${[yearsText(x.years), x.last ? `last worked ${x.last}` : ''].filter(Boolean).join(', ')}`);
  else add('Not given.');
  add(`Resume: ${!m.resume ? 'not on file' : resumePages ? `attached (page${resumePages === 1 ? '' : 's'} 2${resumePages > 1 ? ` to ${resumePages + 1}` : ''})` : 'attached as a separate file'}`, 'head');
  add(REFERENCES_LINE);
  return L;
}

const SIZES = { title: 18, head: 12.5, body: 10.5, small: 9 };
/** Draws summaryLines onto a letter page (wrapped), then the resume: a PDF's pages appended, a JPG / PNG as a page.
 *  -> { bytes, resumePages, resumeSeparate } ; resumeSeparate: a Word resume, downloaded beside the PDF by the caller. */
export async function buildSummary(PDFLib, { model, resume = null }) {
  const { PDFDocument, StandardFonts, rgb } = PDFLib;
  const doc = await PDFDocument.create();
  let resumeDoc = null;
  let image = null;
  if (resume && resume.bytes && model.resume) {
    const ty = model.resume.type.toLowerCase();
    if (ty === 'pdf') { try { resumeDoc = await PDFDocument.load(resume.bytes, { ignoreEncryption: true }); } catch (e) { resumeDoc = null; } }
    else if (ty === 'png') image = await doc.embedPng(resume.bytes);
    else if (ty === 'jpg' || ty === 'jpeg') image = await doc.embedJpg(resume.bytes);
  }
  const resumePages = resumeDoc ? resumeDoc.getPageCount() : image ? 1 : 0;
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const W = 612; const H = 792; const M = 54;
  let page = doc.addPage([W, H]);
  let y = H - M;
  for (const ln of summaryLines(model, { resumePages })) {
    const size = SIZES[ln.style];
    const f = ln.style === 'body' || ln.style === 'small' ? font : bold;
    const words = ln.text.replace(/[^\x20-\x7E -ÿ]/g, '-').split(' ');
    const rows = [];
    let cur = '';
    for (const w of words) { const next = cur ? `${cur} ${w}` : w; if (f.widthOfTextAtSize(next, size) > W - 2 * M && cur) { rows.push(cur); cur = w; } else cur = next; }
    if (cur) rows.push(cur);
    if (ln.style === 'head') y -= 8;
    for (const r of rows) {
      if (y < M + size) { page = doc.addPage([W, H]); y = H - M; }
      page.drawText(r, { x: M, y: y - size, size, font: f, color: ln.style === 'small' ? rgb(0.28, 0.33, 0.41) : rgb(0.06, 0.09, 0.16) });
      y -= size + 5;
    }
  }
  if (resumeDoc) for (const pg of await doc.copyPages(resumeDoc, resumeDoc.getPageIndices())) doc.addPage(pg);
  if (image) {
    const pg = doc.addPage([W, H]);
    const sc = Math.min((W - 2 * M) / image.width, (H - 2 * M) / image.height, 1);
    pg.drawImage(image, { x: M, y: H - M - image.height * sc, width: image.width * sc, height: image.height * sc });
  }
  return { bytes: await doc.save(), resumePages, resumeSeparate: !!(model.resume && !resumePages) };
}

export function makeProfileApi(supabase) {
  const rpc = async (fn, args) => { const { data, error } = await supabase.rpc(fn, args); if (error) throw new Error(plainError(error)); return data; };
  return {
    /** the hub applied the profile file? (the view answers) */
    async probe() { try { const { error } = await supabase.from('profile_status').select('candidate_id', { head: true }).limit(0); return !error; } catch { return false; } },
    async status() { const { data, error } = await supabase.from('profile_status').select('*').limit(5000); if (error) throw new Error(error.message); return data || []; },
    async sessions() {
      const { data, error } = await supabase.from('profile_sessions').select('id,candidate_id,created_at,created_by,expires_at,first_opened_at,revoked_at')
        .order('created_at', { ascending: false }).limit(5000);
      if (error) throw new Error(error.message);
      return data || [];
    },
    /** DAY-DIRECTSTAFF-2026-11-08 §1: the hub's view (2026-11-03-profile-ready-unpresented.sql) -> rows | null (not there / not readable) */
    async readyUnpresented() {
      try { const { data, error } = await supabase.from('profile_ready_unpresented').select('candidate_id,discipline,ready_since,days_ready').limit(500); return error ? null : (data || []); }
      catch { return null; }
    },
    start: (candidateId) => rpc('profile_start', { p_candidate_id: candidateId }),
    relink: (sessionId) => rpc('profile_relink', { p_session_id: sessionId }),
    references: (candidateId) => rpc('profile_references', { p_candidate_id: candidateId }),
    contacted: (candidateId, position) => rpc('profile_reference_contacted', { p_candidate_id: candidateId, p_position: position }),
    async logSent(candidateId, sessionId, channel, by) {
      const { error } = await supabase.from('activity_log').insert({ candidate_id: candidateId, date: new Date().toISOString().slice(0, 10), type: PROFILE_LOG_TYPE, note: profileNote(sessionId, channel, by) });
      if (error) throw new Error(`Sent, but not logged on the timeline: ${error.message}`);
    },
    /** what the summary and the checklist need -- never the signer's IP or browser */
    async profile(candidateId) {
      const { data, error } = await supabase.from('candidate_profiles').select('candidate_id,discipline,skills,settings,skills_attested_at,skills_pdf_path,completed_at,updated_at')
        .eq('candidate_id', candidateId).maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
    async documents(candidateId) {
      const { data, error } = await supabase.from('onboarding_documents').select('id,kind,license_state,expires_on,storage_path,uploaded_at')
        .eq('candidate_id', candidateId).in('kind', ['resume', 'cpr', 'license', 'compact_privilege']).order('uploaded_at', { ascending: false }).limit(200);
      if (error) throw new Error(error.message);
      return data || [];
    },
    async signedUrl(path) {
      const { data, error } = await supabase.storage.from('onboarding-docs').createSignedUrl(path, 60);
      if (error) throw new Error(`The file could not be opened: ${error.message}`);
      return data.signedUrl;
    },
    async logSummary(candidateId, by) {
      const { error } = await supabase.from('activity_log').insert({ candidate_id: candidateId, date: new Date().toISOString().slice(0, 10), type: PROFILE_LOG_TYPE,
        note: `Submission summary downloaded${by ? ` by ${by}` : ''}` });
      if (error) throw new Error(`Downloaded, but not logged on the timeline: ${error.message}`);
    },
  };
}

/** "Download submission summary": the model, the resume's bytes, the PDF; the Word resume comes back to be saved beside it. */
export async function makeSummary({ api, candidate, row, by = '', loadPdfLib = () => import('pdf-lib'), fetchBytes }) {
  const [documents, profile] = await Promise.all([api.documents(candidate.id), api.profile(candidate.id)]);
  const model = summaryModel({ candidate, documents, profile, row });
  let resume = null;
  if (model.resume) resume = { bytes: await fetchBytes(await api.signedUrl(model.resume.path)) };
  const out = await buildSummary(await loadPdfLib(), { model, resume });
  try { await api.logSummary(candidate.id, by); } catch (e) { /* the download stands; the caller says it */ out.logError = e.message; }
  return { ...out, model, resumeBytes: out.resumeSeparate && resume ? resume.bytes : null, filename: `submission-summary-${candidate.id}.pdf` };
}

// ---------------------------------------------------------------------------------------------------------------------------
// DAY-DIRECTSTAFF-2026-10-31 §2: the first-week watch -- one "Profiles" line on Today, from profile_status and the links. Per
// candidate (their newest link): sent (by staff, or by an agent: created_by 'agent:<name>'), opened, started (opened and changed
// since), ready; the oldest not started, in days. "Not visible": a link the agents made for a candidate the CRM does not have
// (no candidates row it loaded) -- it must be none; anything else with an unknown maker is counted too.
// ---------------------------------------------------------------------------------------------------------------------------
export const isAgentLink = (x) => /^agent:[a-z0-9_-]{2,40}$/.test(s(x && x.created_by));
const isStaffLink = (x) => /^[^@\s]+@[^@\s]+$/.test(s(x && x.created_by));
export function profileWatch({ sessions = [], status = [], candidates = [], now = Date.now() }) {
  const known = new Set((candidates || []).map((c) => String(c.id)));
  const st = new Map((status || []).map((r) => [String(r.candidate_id), r]));
  const out = { sent: 0, staff: 0, agent: 0, opened: 0, started: 0, ready: 0, oldestDays: null, unseen: 0, unknownMaker: 0 };
  for (const x of sessions || []) {
    if (isAgentLink(x) && !known.has(String(x.candidate_id))) out.unseen += 1;
    if (!isAgentLink(x) && !isStaffLink(x)) out.unknownMaker += 1;
  }
  for (const [cid, link] of newestLinks(sessions)) {
    const c = (candidates || []).find((k) => String(k.id) === cid);
    if (!c || isTest(c)) continue;
    const r = st.get(cid) || {};
    out.sent += 1;
    if (isAgentLink(link)) out.agent += 1; else out.staff += 1;
    if (link.first_opened_at) out.opened += 1;
    const opened = t(link.first_opened_at);
    if (opened !== null && (t(r.updated_at) || 0) > opened) out.started += 1;
    if (r.submission_ready) out.ready += 1;
    if (!link.first_opened_at && !link.revoked_at && !r.profile_completed_at) {
      const d = Math.floor((now - (t(link.created_at) || now)) / 864e5);
      if (out.oldestDays === null || d > out.oldestDays) out.oldestDays = d;
    }
  }
  return out;
}
/** "Profiles: 5 sent (3 staff, 2 agents) · 3 opened · 2 started · 1 ready · oldest not started 4 days" */
export function watchLine(w) {
  if (!w || !w.sent) return 'Profiles: none sent yet.';
  return [`Profiles: ${w.sent} sent (${w.staff} staff, ${w.agent} agent${w.agent === 1 ? '' : 's'})`, `${w.opened} opened`, `${w.started} started`, `${w.ready} ready`,
    w.oldestDays === null ? 'every link opened' : `oldest not started ${w.oldestDays} day${w.oldestDays === 1 ? '' : 's'}`].join(' · ');
}

// DAY-DIRECTSTAFF-2026-10-31 §3: the checklists Matt had not approved: a candidate of those disciplines saw "isn't open yet" on their
// checklist step, so the block said why. 2026-11-03: he approved all five -- none awaits (the line stays for any later draft).
export const AWAITING = [];
/** '' | "Skills checklist for OT: awaiting Matt's approval of the draft. Until then their checklist step says it isn't open yet." */
export function awaitingLine(candidate, row) {
  const d = s((row && row.discipline) || (candidate && candidate.specialty)).toUpperCase();
  if (!AWAITING.includes(d) || CHECKLISTS[d]) return '';
  return `Skills checklist for ${d}: awaiting Matt's approval of the draft. Until then their checklist step says it isn't open yet.`;
}
